import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { Helmet } from "react-helmet"
import JSONLDSchema from "./seo/schema"

const Seo = ({ seo = {} }) => {
  const { strapiGlobal } = useStaticQuery(graphql`
    query {
      strapiGlobal {
        siteName
        defaultSeo {
          metaTitle
          metaDescription
        }
      }
    }
  `)

  const { siteName, defaultSeo } = strapiGlobal

  // Merge default and page-specific SEO values
  const fullSeo = { ...defaultSeo, ...seo }

  // Add site name to title
  fullSeo.metaTitle = fullSeo.metaTitle ? `${fullSeo.metaTitle} | ${siteName}` : siteName

  const getMetaTags = () => {
    const tags = []
    
    // URL canónica - importante para SEO y compartir en redes sociales
    const currentURL = typeof window !== 'undefined' ? `${window.location.origin}${window.location.pathname}` : ''

    if (fullSeo.metaTitle) {
      tags.push(
        {
          property: "og:title",
          content: fullSeo.metaTitle,
        },
        {
          name: "twitter:title",
          content: fullSeo.metaTitle,
        }
      )
    }
    if (fullSeo.metaDescription) {
      tags.push(
        {
          name: "description",
          content: fullSeo.metaDescription,
        },
        {
          property: "og:description",
          content: fullSeo.metaDescription,
        },
        {
          name: "twitter:description",
          content: fullSeo.metaDescription,
        }
      )
    } else {
      // Proporcionar una meta descripción predeterminada si no hay una específica
      const defaultDescription = "Claus: La solución integral para gestionar tus facturas electrónicas y optimizar tus finanzas empresariales. Simplifica tu contabilidad, cumple con tus procesos fiscales con nuestro software contable digital."
      tags.push(
        {
          name: "description",
          content: defaultDescription,
        },
        {
          property: "og:description",
          content: defaultDescription,
        },
        {
          name: "twitter:description",
          content: defaultDescription,
        }
      )
    }
    if (fullSeo.shareImage) {
      const imageUrl = fullSeo.shareImage.localFile.url
      const imageAlt = fullSeo.imageAlt || fullSeo.metaTitle || siteName
      
      tags.push(
        {
          name: "image",
          content: imageUrl,
        },
        {
          property: "og:image",
          content: imageUrl,
        },
        {
          property: "og:image:width",
          content: "1200",
        },
        {
          property: "og:image:height",
          content: "630",
        },
        {
          property: "og:image:alt",
          content: imageAlt,
        },
        {
          name: "twitter:image",
          content: imageUrl,
        },
        {
          name: "twitter:image:alt",
          content: imageAlt,
        }
      )
    }
    if (fullSeo.article) {
      tags.push({
        property: "og:type",
        content: "article",
      })
    }
    // Añadir palabras clave relevantes para mejorar el SEO
    const keywords = "facturación electrónica, factura electrónica, software contable, gestión fiscal, contabilidad digital, finanzas empresariales, impuestos"
    
    // Definir og:type por defecto como 'website' a menos que sea un artículo
    if (!fullSeo.article) {
      tags.push({
        property: "og:type",
        content: "website",
      })
    }
    
    // Añadir URL canónica a las metaetiquetas
    tags.push(
      { property: "og:url", content: currentURL },
      { name: "twitter:url", content: currentURL }
    )
    
    // Metaetiquetas generales y de redes sociales
    tags.push(
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@clausco_co" },
      { name: "twitter:creator", content: "@clausco_co" },
      { name: "robots", content: "index, follow" },
      { name: "language", content: "Spanish" },
      { property: "og:locale", content: "es_ES" },
      { property: "og:site_name", content: siteName },
      { name: "keywords", content: keywords },
      // Metaetiquetas para Facebook
      { property: "fb:pages", content: "112233445566778" },
      // Etiquetas adicionales para WhatsApp y otras plataformas de mensajería
      { name: "format-detection", content: "telephone=no" }
    )

    return tags
  }

  const metaTags = getMetaTags()

  // Determinar qué tipo de esquema de datos estructurados usar según la página actual
  const getSchemaType = () => {
    if (typeof window === 'undefined') return 'WebSite';
    
    const path = window.location.pathname;
    
    if (path === '/' || path === '') {
      return 'WebSite';
    } else if (path.includes('/producto')) {
      return 'SoftwareApplication';
    } else if (path.includes('/blog') || path.includes('/article')) {
      return 'Article';
    } else {
      return 'Organization';
    }
  }

  // Preparar datos para el esquema seleccionado
  const getSchemaData = () => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
    
    // Datos básicos para cualquier tipo de esquema
    const commonData = {
      description: fullSeo.metaDescription,
      url: baseUrl + currentPath
    };
    
    // Datos específicos para aplicaciones de software
    if (getSchemaType() === 'SoftwareApplication') {
      return {
        ...commonData,
        name: fullSeo.metaTitle || 'Claus - Software Contable y Facturación Electrónica',
        applicationCategory: 'BusinessApplication',
        offers: {
          price: '0',
          priceCurrency: 'COP'
        }
      };
    }
    
    // Usar los datos comunes para otros tipos de esquema
    return commonData;
  }
  
  return (
    <>
      <Helmet
        title={fullSeo.metaTitle}
        link={[
          {
            rel: "canonical",
            href: typeof window !== 'undefined' ? window.location.origin + (typeof window !== 'undefined' ? window.location.pathname : '') : '',
          },
        ]}
        meta={metaTags}
      />
      
      {/* Implementar datos estructurados con el componente modular */}
      <JSONLDSchema 
        type={getSchemaType()} 
        data={getSchemaData()}
        scriptKey={`structured-data-${getSchemaType().toLowerCase()}`} 
      />
      
      {/* Añadir también el esquema de Organization en todas las páginas */}
      {getSchemaType() !== 'Organization' && (
        <JSONLDSchema 
          type="Organization" 
          scriptKey="structured-data-organization"
        />
      )}
    </>
  )
}

export default Seo